import React from 'react';
import { Navigate } from 'react-router-dom';
import { User } from '../../types';
import AppLayout, { AppLayoutProps } from './AppLayout';

interface ProtectedRouteProps {
  user: User | null;
  token: string | null;
  requireSubscription?: boolean;
  checkFreeSubscription?: boolean;
  layoutProps: Omit<AppLayoutProps, 'children'>;
  children: React.ReactNode;
  isReturningFromCheckout: boolean;
}

// Check subscription status
const hasActiveSubscription = (user: User | null): boolean => {
  if (!user) return false;
  return user.subscriptionStatus === 'active' || user.subscriptionStatus === 'trialing';
};

// Check if user has free subscription
const hasFreeSubscription = (user: User | null): boolean => {
  if (!user) return false;
  return (
    user.subscriptionStatus === 'free' ||
    user.subscriptionStatus === null ||
    user.subscriptionStatus === undefined
  );
};

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  user,
  token,
  requireSubscription = false,
  checkFreeSubscription = false,
  layoutProps,
  children,
  isReturningFromCheckout,
}) => {
  // Not authenticated
  if (!user || !token) {
    return <Navigate to="/login" replace />;
  }

  // Check free subscription redirect
  if (checkFreeSubscription && hasFreeSubscription(user) && !isReturningFromCheckout) {
    return <Navigate to="/free-plan" replace />;
  }

  // Check active subscription requirement
  if (requireSubscription && !hasActiveSubscription(user) && !isReturningFromCheckout) {
    return <Navigate to="/register/pricing" replace />;
  }

  return <AppLayout {...layoutProps}>{children}</AppLayout>;
};

export default ProtectedRoute;
